'use client'

import { useEffect, useState, useRef } from 'react'
import isMobile from 'ismobilejs'
import Spline from '@splinetool/react-spline'
import styles from './styles.module.scss'
import { ANIMATION_DETAILS, MAX_RETRY_ATTEMPTS, RETRY_DELAY } from './config'

const FloatAnimation = ({ slug }: { slug: string }) => {
  const animationConfig = ANIMATION_DETAILS[slug]
  const [elementsReady, setElementsReady] = useState(false)
  const [splineLoaded, setSplineLoaded] = useState(false)
  const [retryCount, setRetryCount] = useState(0)
  const [shouldRenderSpline, setShouldRenderSpline] = useState(true)
  const [webgl2Supported, setWebgl2Supported] = useState<boolean | null>(null)
  const wrapperRef = useRef<HTMLDivElement>(null)
  const animationFrameRef = useRef<number>()
  const resizeObserverRef = useRef<ResizeObserver | null>(null)
  const intersectionObserverRef = useRef<IntersectionObserver | null>(null)
  const imageLoadCleanupsRef = useRef<Array<() => void>>([])
  const isMountedRef = useRef(true)
  const [shouldHide, setShouldHide] = useState(false)
  const shouldHideRef = useRef(false)

  useEffect(() => {
    if (!animationConfig) return

    const THRESHOLD = animationConfig.threshold || 1800

    const onScroll = () => {
      const shouldHide = window.scrollY > THRESHOLD

      if (shouldHide !== shouldHideRef.current) {
        shouldHideRef.current = shouldHide
        setShouldHide(shouldHide)
      }
    }

    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [animationConfig])

  useEffect(() => {
    if (elementsReady) return

    const checkElementsReady = () => {
      const startElement = document.getElementById('spline-start-position')
      const endElement = document.getElementById('spline-end-position')

      if (!startElement || !endElement) return false

      const startRect = startElement.getBoundingClientRect()
      const endRect = endElement.getBoundingClientRect()

      if (startRect.width === 0 || startRect.height === 0) return false

      if (endElement instanceof HTMLImageElement && endElement.loading === 'lazy' && !endElement.complete) {
        if (!intersectionObserverRef.current) {
          intersectionObserverRef.current = new IntersectionObserver(
            entries => {
              entries.forEach(entry => {
                if (entry.isIntersecting) {
                  const onImageLoad = () => {
                    const rect = endElement.getBoundingClientRect()
                    if (rect.width > 0 && rect.height > 0) {
                      setElementsReady(true)
                    }
                    endElement.removeEventListener('load', onImageLoad)
                  }
                  endElement.addEventListener('load', onImageLoad)
                  imageLoadCleanupsRef.current.push(() => endElement.removeEventListener('load', onImageLoad))
                  intersectionObserverRef.current?.disconnect()
                }
              })
            },
            { rootMargin: '200px' }
          )
          intersectionObserverRef.current.observe(endElement)
        }
        return false
      }

      return endRect.width > 0 && endRect.height > 0
    }

    const checkAndSetReady = () => {
      if (document.readyState === 'complete' && checkElementsReady()) {
        setElementsReady(true)
      }
    }

    if (document.readyState === 'complete') {
      if (checkElementsReady()) {
        setElementsReady(true)
        return
      }
    } else {
      const onWindowLoad = () => checkAndSetReady()
      window.addEventListener('load', onWindowLoad)
      return () => window.removeEventListener('load', onWindowLoad)
    }

    const intervalId = setInterval(checkAndSetReady, 100)
    const timeoutId = setTimeout(() => clearInterval(intervalId), 10000)

    return () => {
      clearInterval(intervalId)
      clearTimeout(timeoutId)
      intersectionObserverRef.current?.disconnect()
      imageLoadCleanupsRef.current.forEach(cleanup => cleanup())
    }
  }, [elementsReady])

  useEffect(() => {
    isMountedRef.current = true

    const checkWebGL2Support = (): boolean => {
      try {
        const canvas = document.createElement('canvas')
        const gl = canvas.getContext('webgl2')
        return gl !== null && typeof gl.clearBufferfv === 'function'
      } catch {
        return false
      }
    }

    const supportsWebGL2 = checkWebGL2Support()
    setWebgl2Supported(supportsWebGL2)

    if (!supportsWebGL2) {
      console.warn('WebGL2 is not supported. Spline animation will not render.')
      setShouldRenderSpline(false)
    }

    return () => {
      isMountedRef.current = false
    }
  }, [])

  useEffect(() => {
    if (!elementsReady || !splineLoaded || !wrapperRef.current || !animationConfig) return

    const wrapper = wrapperRef.current
    const startTopOffset = animationConfig.offsetPositions.startTopOffset
    const startLeftOffset = animationConfig.offsetPositions.startLeftOffset
    const endTopOffset = animationConfig.offsetPositions.endTopOffset
    const endLeftOffset = animationConfig.offsetPositions.endLeftOffset

    const updatePosition = () => {
      const startElement = document.getElementById('spline-start-position')
      const endElement = document.getElementById('spline-end-position')

      if (!startElement || !endElement) return

      const startRect = startElement.getBoundingClientRect()
      const endRect = endElement.getBoundingClientRect()

      if (startRect.width === 0 || startRect.height === 0 || endRect.width === 0 || endRect.height === 0) {
        return
      }

      const scrollY = window.scrollY || window.pageYOffset
      const startTop = startRect.top + startRect.height * startTopOffset
      const startLeft = window.innerWidth / 2 + startRect.width * (startLeftOffset - 0.5)
      const endTop = endRect.top + endRect.height * endTopOffset
      const endLeft = endRect.left + endRect.width * endLeftOffset

      const startElementTop = startRect.top + scrollY
      const endElementTop = endRect.top + scrollY
      const animationStart = startElementTop
      const animationEnd = endElementTop
      const scrollRange = animationEnd - animationStart

      let progress = 0
      if (Math.abs(scrollRange) > 0.1) {
        if (scrollRange > 0) {
          if (scrollY < animationStart) {
            progress = 0
          } else if (scrollY > animationEnd) {
            progress = 1
          } else {
            progress = (scrollY - animationStart) / scrollRange
          }
        } else if (scrollY > animationStart) {
          progress = 0
        } else if (scrollY < animationEnd) {
          progress = 1
        } else {
          progress = (scrollY - animationStart) / scrollRange
        }
      }

      progress = Math.max(0, Math.min(1, progress))

      const currentTop = startTop + (endTop - startTop) * progress
      const currentLeft = startLeft + (endLeft - startLeft) * progress

      wrapper.style.top = `${currentTop}px`
      wrapper.style.left = `${currentLeft}px`
      wrapper.style.transform = 'translate(-50%, -50%)'

      const fadeStart = 0.75
      const fadeProgress = Math.max(0, (progress - fadeStart) / (1 - fadeStart))
      wrapper.style.opacity = progress >= animationConfig.hideProgress ? '0' : `${1 - fadeProgress}`

      if (progress >= animationConfig.hideProgress) {
        wrapper.style.display = 'none'
      } else {
        wrapper.style.display = 'block'
      }
    }

    const observeElementChanges = () => {
      const startElement = document.getElementById('spline-start-position')
      const endElement = document.getElementById('spline-end-position')

      if (!startElement || !endElement) return

      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect()
      }

      resizeObserverRef.current = new ResizeObserver(() => {
        if (animationFrameRef.current) {
          cancelAnimationFrame(animationFrameRef.current)
        }
        animationFrameRef.current = requestAnimationFrame(updatePosition)
      })

      resizeObserverRef.current.observe(startElement)
      resizeObserverRef.current.observe(endElement)

      const elements = [startElement, endElement]
      elements.forEach(element => {
        if (element instanceof HTMLImageElement && !element.complete) {
          const onLoad = () => updatePosition()
          element.addEventListener('load', onLoad)
          imageLoadCleanupsRef.current.push(() => element.removeEventListener('load', onLoad))
        }
      })
    }

    const handleScroll = () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current)
      }
      animationFrameRef.current = requestAnimationFrame(updatePosition)
    }

    updatePosition()
    observeElementChanges()

    requestAnimationFrame(() => {
      updatePosition()
      window.dispatchEvent(new Event('scroll'))
    })

    window.addEventListener('scroll', handleScroll, { passive: true })
    window.addEventListener('resize', handleScroll, { passive: true })

    return () => {
      window.removeEventListener('scroll', handleScroll)
      window.removeEventListener('resize', handleScroll)
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current)
      }
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect()
      }
      imageLoadCleanupsRef.current.forEach(cleanup => cleanup())
    }
  }, [elementsReady, splineLoaded, animationConfig])

  const handleSplineLoad = () => {
    if (!isMountedRef.current) return
    setSplineLoaded(true)
    window.dispatchEvent(new Event('scroll'))
  }

  const handleSplineError = (event: React.SyntheticEvent<HTMLDivElement> | Error) => {
    const error = event instanceof Error ? event : (event.target as HTMLDivElement)
    console.error('Spline scene failed to load:', error)

    if (!isMountedRef.current) return

    const errorMessage = error instanceof Error ? error.message : String(error)
    if (errorMessage.includes('clearBufferfv') || errorMessage.includes('WebGL')) {
      setShouldRenderSpline(false)
      setSplineLoaded(true)
      return
    }

    if (retryCount < MAX_RETRY_ATTEMPTS) {
      setRetryCount(prev => prev + 1)
      setSplineLoaded(false)
      setShouldRenderSpline(false)

      setTimeout(() => {
        if (isMountedRef.current) {
          setShouldRenderSpline(true)
        }
      }, RETRY_DELAY)
    } else {
      setSplineLoaded(true)
    }
  }

  if (isMobile()?.phone || isMobile()?.tablet) return null
  if (!elementsReady) return null
  if (!animationConfig) return null

  return (
    <div ref={wrapperRef} className={`${styles.animationWrapper} ${shouldHide ? styles.hideAnimation : ''}`}>
      {shouldRenderSpline && webgl2Supported !== false && (
        <Spline scene={animationConfig.spline} onLoad={handleSplineLoad} onError={handleSplineError} />
      )}
    </div>
  )
}

export default FloatAnimation
